import { alpha, Box, Paper, Stack } from "@mui/material";
import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import CardHeaderTitle from "../../components/card-header-title/index.jsx";
import { AppChartCirclePattern, AppChartWavePattern } from "../../components/chart/chart-pattern.jsx";
import LegendItemGroup from "../../components/legend-item-group/index.jsx";
import CustomSvgIcons from "../../components/svg-icons/index.jsx";
import { useThemeMode } from "../../components/theme/index.jsx";
import { ChartTooltip } from "../../consts.js";
import { THEME_MODES } from "../../helpers/constant.js";

const SentimentChart = ({ sentimentData }) => {
    const { themeMode, colours, FONT } = useThemeMode();

    const barColors = [
        [colours.success100, colours.success100],
        [colours.dividergray, colours.dividergray],
        [colours.critical100, colours.critical100],
    ];

    // created an array of 0 to 100.
    const ticks = Array(11)
        .fill(0)
        .reduce((acc, _, index) => {
            acc.push(index * 10);
            return acc;
        }, []);

    return (
        <Paper
            sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
        >
            <Stack
                direction="row"
                flexWrap="wrap"
                gap={{ xs: 0, sm: 2 }}
                justifyContent="space-between"
                alignItems="center"
            >
                <CardHeaderTitle
                    title="Overall Sentiment"
                    tooltip="Sentiment indicates the attitude or tone of the Speaker at a point in the Conversation. It can be positive or negative."
                />

                <LegendItemGroup
                    circleSize={themeMode === THEME_MODES.COLOR_VISION ? 24 : 16}
                    items={[
                        { label: "Neutral", color: colours.dividergray },
                        { label: "Negative", color: colours.critical100, pattern: themeMode === THEME_MODES.COLOR_VISION && CustomSvgIcons.negativePattern },
                        { label: "Positive", color: colours.success100, pattern: themeMode === THEME_MODES.COLOR_VISION && CustomSvgIcons.positivePattern },
                    ]}
                />

            </Stack>
            {/* <div
                style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "5px",
                }}
                >
                <IncreaseDecreaseArrows 
                    increase={true}
                    percentage="3%"
                    size={24}
                    percentageFontSize={FONT.SIZE_24}
                    percentageColor={colours.black}
                />
                <ButtonGroupComponent colours={colours} themeMode={themeMode} />
            </div> */}
            <Box width="100%" height={255} marginTop={2.5}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width="100%"
                        height="100%"
                        data={sentimentData}
                        margin={{
                            top: 0,
                            right: 0,
                            left: -20,
                            bottom: -12,
                        }}
                    >
                        <CartesianGrid vertical={false} stroke={alpha(colours.black, 0.1)} />
                        <XAxis
                            dataKey="name"
                            axisLine={{ stroke: alpha(colours.black, 0.1) }}
                            tickLine={{ strokeWidth: 0 }}
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={FONT.SIZE_12}
                            tick={{
                                fill: colours.black,
                                fontSize: FONT.SIZE_12,
                                fontFamily: "'Poppins', sans-serif",
                            }}
                        />
                        <YAxis
                            axisLine={{ stroke: alpha(colours.black, 0.1) }}
                            tickLine={{ strokeWidth: 0 }}
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={FONT.SIZE_12}
                            minTickGap={20}
                            tick={{
                                fill: colours.black,
                                fontSize: FONT.SIZE_12,
                                fontFamily: "'Poppins', sans-serif",
                            }}
                            ticks={ticks}
                            type="number"
                            domain={[0, 100]}
                            tickFormatter={(d) => d.toFixed(0) + "%"}
                        />
                        <Tooltip
                            formatter={(v) => v.toFixed(0) + "%"}
                            contentStyle={ChartTooltip(colours, FONT)}
                            itemStyle={{ color: colours.white }}
                            cursor={false}
                        />
                        <defs>
                            {barColors.map((color, index) => (
                                <linearGradient
                                    id={`colorUv${index}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="100%"
                                    spreadMethod="reflect"
                                    key={`colorUv${index}`}
                                >
                                    <stop offset="0" stopColor={color[0]} />
                                    <stop offset="1" stopColor={color[1]} />
                                </linearGradient>
                            ))}
                        </defs>
                        <defs>
                            {barColors.map((color, index) => (
                                <AppChartWavePattern key={`wave-${index}`} patternId={"chartWave"} color={color[0]} index={index} />
                            ))}
                        </defs>
                        <defs>
                            ({barColors.map((color, index) => (
                                <AppChartCirclePattern key={`circle-${index}`} patternId={"chartCircle"} color={color[0]} index={index} />
                            ))})
                        </defs>
                        <Bar
                            dataKey="Positive"
                            stackId="a"
                            fill={colours.green100}
                            radius={0}
                            maxBarSize={80}
                        >
                            {sentimentData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={themeMode === THEME_MODES.COLOR_VISION ? `url(#chartCircle0)` : `url(#colorUv0)`} />
                            ))}
                        </Bar>
                        <Bar
                            dataKey="Neutral"
                            stackId="a"
                            fill={colours.subduedgray}
                            radius={0}
                            maxBarSize={80}
                        >
                            {sentimentData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={`url(#colorUv1)`} />
                            ))}
                        </Bar>
                        <Bar
                            dataKey="Negative"
                            stackId="a"
                            fill={colours.primary100}
                            radius={[8, 8, 0, 0]}
                            maxBarSize={80}
                        >
                            {sentimentData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={themeMode === THEME_MODES.COLOR_VISION ? `url(#chartWave2)` : `url(#colorUv2)`} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
};

export default SentimentChart;
